/**
 * ============================================
 * PLACEHOLDER PAGE - Página Temporal
 * ============================================
 *
 * Componente reutilizable para páginas que aún no están implementadas.
 * Muestra el nombre del módulo y un mensaje indicando que está en desarrollo.
 *
 * ACTUALIZACIÓN: Ahora usa i18next para traducciones dinámicas.
 * - El título usa una clave de traducción (ej: "nav.pacientes")
 * - La descripción también usa una clave de traducción
 */

import { Construction } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PlaceholderPageProps {
  /** Clave de traducción para el título (ej: "nav.pacientes") */
  titleKey: string;
  /** Clave de traducción para la descripción (ej: "pacientes.title") */
  descriptionKey: string;
  /** Icono del módulo (de lucide-react) */
  icon: LucideIcon;
}

export function PlaceholderPage({ titleKey, descriptionKey, icon: Icon }: PlaceholderPageProps) {
  // Hook de i18next para obtener traducciones
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      {/* Icono del módulo */}
      <div className="mb-4 p-4 rounded-full bg-muted">
        <Icon className="h-12 w-12 text-muted-foreground" />
      </div>

      {/* Título - traducido dinámicamente */}
      <h1 className="text-2xl font-bold mb-2">{t(titleKey)}</h1>

      {/* Descripción - traducida dinámicamente */}
      <p className="text-muted-foreground mb-6 max-w-md">{t(descriptionKey)}</p>

      {/* Indicador de "en construcción" - también traducido */}
      <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950 dark:text-amber-400 px-4 py-2 rounded-lg">
        <Construction className="h-4 w-4" />
        <span>{t("common.moduleInDevelopment")}</span>
      </div>
    </div>
  );
}
